import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  BookOpen, 
  Clock, 
  Target, 
  Play,
  Bookmark,
  Share2,
  Video,
  Gamepad2
} from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import toast from 'react-hot-toast'

interface BaiHoc {
  id: string
  title: string
  description: string
  time: number
  difficulty: 'easy' | 'medium' | 'hard'
  sections: {
    lyThuyet: boolean
    video: boolean
    tracNghiem: boolean
    moPhong: boolean
  }
  completed: boolean
  bookmarked: boolean
}

interface Chuong {
  id: number
  title: string
  description: string
  color: string
  baiHoc: BaiHoc[]
}

const DanhSachChuong: React.FC = () => {
  const { idLop, idChuong } = useParams<{ idLop: string; idChuong: string }>()
  const navigate = useNavigate()
  
  const [currentChuong, setCurrentChuong] = useState<Chuong | null>(null)
  const [baiHoc, setBaiHoc] = useState<BaiHoc[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'lyThuyet' | 'video' | 'tracNghiem' | 'moPhong'>('all')

  // Cấu trúc chương theo chương trình Vật lí THPT - LỚP 10
  const chuongData: Chuong[] = [
    {
      id: 0,
      title: 'MỞ ĐẦU',
      description: 'Giới thiệu mục đích học tập môn Vật lí',
      color: 'from-violet-50 to-violet-100',
      baiHoc: [
        {
          id: 'bai-1-gioi-thieu-muc-dich-hoc-tap-mon-vat-li',
          title: 'Bài 1. Giới thiệu mục đích học tập môn Vật lí',
          description: 'Nội dung bài học về giới thiệu mục đích học tập môn Vật lí trong mở đầu',
          time: 30,
          difficulty: 'easy',
          sections: { lyThuyet: true, video: false, tracNghiem: true, moPhong: false },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 1,
      title: 'ĐỘNG HỌC',
      description: 'Nghiên cứu chuyển động của các vật thể',
      color: 'from-sky-50 to-sky-100',
      baiHoc: [
        {
          id: 'bai-2-do-dich-chuyen-va-quang-duong-di-duoc',
          title: 'Bài 2. Độ dịch chuyển và quãng đường đi được',
          description: 'Khái niệm về độ dịch chuyển và quãng đường trong chuyển động',
          time: 45,
          difficulty: 'easy',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-3-toc-do-va-van-toc-chuyen-dong-tong-hop',
          title: 'Bài 3. Tốc độ và vận tốc - Chuyển động tổng hợp',
          description: 'Phân biệt tốc độ và vận tốc, chuyển động tổng hợp',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-4-do-thi-do-dich-chuyen-thoi-gian',
          title: 'Bài 4. Đồ thị độ dịch chuyển – thời gian',
          description: 'Cách đọc và vẽ đồ thị chuyển động',
          time: 40,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-5-chuyen-dong-bien-doi',
          title: 'Bài 5. Chuyển động biến đổi',
          description: 'Chuyển động có gia tốc thay đổi',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-6-chuyen-dong-roi-tu-do',
          title: 'Bài 6. Chuyển động rơi tự do',
          description: 'Chuyển động rơi dưới tác dụng của trọng lực',
          time: 45,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-7-chuyen-dong-nem',
          title: 'Bài 7. Chuyển động ném',
          description: 'Chuyển động ném ngang và ném xiên',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 2,
      title: 'ĐỘNG LỰC HỌC',
      description: 'Nghiên cứu nguyên nhân gây ra chuyển động',
      color: 'from-emerald-50 to-emerald-100',
      baiHoc: [
        {
          id: 'bai-8-ba-dinh-luat-newton-ve-chuyen-dong',
          title: 'Bài 8. Ba định luật Newton về chuyển động',
          description: 'Các định luật cơ bản của động lực học',
          time: 70,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-9-mot-so-luc-trong-thuc-tien',
          title: 'Bài 9. Một số lực trong thực tiễn',
          description: 'Các loại lực thường gặp trong đời sống',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-10-tong-hop-luc-va-phan-tich-luc',
          title: 'Bài 10. Tổng hợp lực và phân tích lực',
          description: 'Phương pháp tổng hợp và phân tích lực',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-11-can-bang-luc-moment-luc',
          title: 'Bài 11. Cân bằng lực, moment lực',
          description: 'Điều kiện cân bằng và moment lực',
          time: 55,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-12-khoi-luong-rieng-ap-suat-chat-long',
          title: 'Bài 12. Khối lượng riêng, áp suất chất lỏng',
          description: 'Tính chất của chất lỏng và áp suất',
          time: 45,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 3,
      title: 'NĂNG LƯỢNG VÀ CÔNG',
      description: 'Nghiên cứu về năng lượng và công cơ học',
      color: 'from-orange-50 to-orange-100',
      baiHoc: [
        {
          id: 'bai-13-cong-va-nang-luong',
          title: 'Bài 13. Công và năng lượng',
          description: 'Khái niệm công cơ học và các dạng năng lượng',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-14-dong-nang-va-the-nang',
          title: 'Bài 14. Động năng và thế năng',
          description: 'Các dạng năng lượng cơ học',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-15-cong-suat-va-hieu-suat',
          title: 'Bài 15. Công suất và hiệu suất',
          description: 'Công suất thực hiện và hiệu suất chuyển đổi năng lượng',
          time: 45,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-16-dong-luong-dinh-luat-bao-toan-dong-luong',
          title: 'Bài 16. Động lượng, định luật bảo toàn động lượng',
          description: 'Động lượng và sự bảo toàn trong va chạm',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-17-dong-luong-va-va-cham',
          title: 'Bài 17. Động lượng và va chạm',
          description: 'Phân loại va chạm và ứng dụng định luật bảo toàn',
          time: 55,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 4,
      title: 'CHUYỂN ĐỘNG TRÒN VÀ BIẾN DẠNG',
      description: 'Nghiên cứu chuyển động tròn và biến dạng vật thể',
      color: 'from-purple-50 to-purple-100',
      baiHoc: [
        {
          id: 'bai-18-dong-hoc-chuyen-dong-tron-deu',
          title: 'Bài 18. Động học của chuyển động tròn đều',
          description: 'Đặc điểm của chuyển động tròn đều',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-19-gia-toc-huong-tam-va-luc-huong-tam',
          title: 'Bài 19. Gia tốc hướng tâm và lực hướng tâm',
          description: 'Gia tốc và lực trong chuyển động tròn',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-20-su-bien-dang-dinh-luat-hooke',
          title: 'Bài 20. Sự biến dạng, định luật Hooke',
          description: 'Biến dạng đàn hồi và định luật Hooke',
          time: 45,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    }
  ]

  // Cấu trúc cho lớp 11
  const chuongDataLop11: Chuong[] = [
    {
      id: 1,
      title: 'DAO ĐỘNG',
      description: 'Nghiên cứu dao động điều hòa và các loại dao động',
      color: 'from-slate-50 to-slate-100',
      baiHoc: [
        {
          id: 'bai-1-dao-dong-dieu-hoa-phuong-trinh-dao-dong-dieu-hoa',
          title: 'Bài 1. Dao động điều hòa – Phương trình dao động điều hoà',
          description: 'Khái niệm và phương trình của dao động điều hòa',
          time: 60,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-2-van-toc-gia-toc-trong-dao-dong-dieu-hoa',
          title: 'Bài 2. Vận tốc, gia tốc trong dao động điều hòa',
          description: 'Tính toán vận tốc và gia tốc trong dao động',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-3-nang-luong-trong-dao-dong-dieu-hoa',
          title: 'Bài 3. Năng lượng trong dao động điều hòa',
          description: 'Sự chuyển hóa năng lượng trong dao động',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-4-dao-dong-tat-dan-dao-dong-cuong-buc-hien-tuong-cong-huong',
          title: 'Bài 4. Dao động tắt dần, dao động cưỡng bức, hiện tượng cộng hưởng',
          description: 'Các loại dao động và hiện tượng cộng hưởng',
          time: 65,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 2,
      title: 'SÓNG',
      description: 'Nghiên cứu sóng cơ và sóng điện từ',
      color: 'from-gray-50 to-gray-100',
      baiHoc: [
        {
          id: 'bai-5-mo-ta-song-su-truyen-song',
          title: 'Bài 5. Mô tả sóng - Sự truyền sóng',
          description: 'Khái niệm cơ bản về sóng và cơ chế truyền sóng',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-6-cac-dac-trung-cua-song-song-doc-va-song-ngang',
          title: 'Bài 6. Các đặc trưng của sóng - Sóng dọc và sóng ngang',
          description: 'Phân loại sóng và các đặc trưng cơ bản',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-7-song-dien-tu',
          title: 'Bài 7. Sóng điện từ',
          description: 'Đặc điểm và ứng dụng của sóng điện từ',
          time: 60,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-8-giao-thoa-song',
          title: 'Bài 8. Giao thoa sóng',
          description: 'Hiện tượng giao thoa sóng',
          time: 65,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-9-song-dung',
          title: 'Bài 9. Sóng dừng',
          description: 'Hiện tượng sóng dừng và ứng dụng',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-10-do-toc-do-truyen-am',
          title: 'Bài 10. Đo tốc độ truyền âm',
          description: 'Phương pháp đo tốc độ truyền âm',
          time: 45,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 3,
      title: 'ĐIỆN TRƯỜNG',
      description: 'Nghiên cứu điện trường và tương tác điện',
      color: 'from-blue-50 to-blue-100',
      baiHoc: [
        {
          id: 'bai-11-luc-tuong-tac-giua-cac-dien-tich-dinh-luat-coulomb',
          title: 'Bài 11. Lực tương tác giữa các điện tích – ĐL Coulomb',
          description: 'Định luật Coulomb và lực tương tác điện',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-12-khai-niem-dien-truong-dien-truong-deu',
          title: 'Bài 12. Khái niệm điện trường, điện trường đều',
          description: 'Khái niệm và đặc điểm của điện trường',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-13-dien-the-va-the-nang-dien',
          title: 'Bài 13. Điện thế và thế năng điện',
          description: 'Điện thế và thế năng trong điện trường',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-14-dien-dung-va-tu-dien-nang-luong-dien-truong',
          title: 'Bài 14. Điện dung và tụ điện – Năng lượng điện trường',
          description: 'Tụ điện và năng lượng tích trữ',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 4,
      title: 'DÒNG ĐIỆN, MẠCH ĐIỆN',
      description: 'Nghiên cứu dòng điện và mạch điện',
      color: 'from-indigo-50 to-indigo-100',
      baiHoc: [
        {
          id: 'bai-15-dong-dien-cuong-do-dong-dien',
          title: 'Bài 15. Dòng điện - Cường độ dòng điện',
          description: 'Khái niệm dòng điện và cường độ dòng điện',
          time: 45,
          difficulty: 'easy',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-16-dien-tro-dinh-luat-ohm',
          title: 'Bài 16. Điện trở, định luật Ohm',
          description: 'Điện trở và định luật Ohm',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-17-nguon-dien',
          title: 'Bài 17. Nguồn điện',
          description: 'Các loại nguồn điện và suất điện động',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-18-nang-luong-dien-cong-suat-dien',
          title: 'Bài 18. Năng lượng điện, công suất điện',
          description: 'Năng lượng và công suất trong mạch điện',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    }
  ]

  // Cấu trúc cho lớp 12
  const chuongDataLop12: Chuong[] = [
    {
      id: 1,
      title: 'VẬT LÍ NHIỆT',
      description: 'Nghiên cứu các hiện tượng nhiệt',
      color: 'from-rose-50 to-rose-100',
      baiHoc: [
        {
          id: 'bai-1-su-chuyen-the',
          title: 'Bài 1. Sự chuyển thể',
          description: 'Các quá trình chuyển thể của vật chất',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-2-noi-nang-dinh-luat-1-cua-nhiet-dong-luc-hoc',
          title: 'Bài 2. Nội năng, định luật 1 của nhiệt động lực học',
          description: 'Nội năng và định luật bảo toàn năng lượng',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-3-thang-nhiet-do-nhiet-ke',
          title: 'Bài 3. Thang nhiệt độ, nhiệt kế',
          description: 'Các thang nhiệt độ và dụng cụ đo nhiệt',
          time: 40,
          difficulty: 'easy',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-4-nhiet-dung-rieng',
          title: 'Bài 4. Nhiệt dung riêng',
          description: 'Nhiệt dung riêng và khả năng hấp thụ nhiệt',
          time: 45,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-5-nhiet-nong-chay-rieng',
          title: 'Bài 5. Nhiệt nóng chảy riêng',
          description: 'Nhiệt nóng chảy và quá trình chuyển thể',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-6-nhiet-hoa-hoi-rieng',
          title: 'Bài 6. Nhiệt hóa hơi riêng',
          description: 'Nhiệt hóa hơi và quá trình bay hơi',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 2,
      title: 'KHÍ LÍ TƯỞNG',
      description: 'Nghiên cứu tính chất của khí lí tưởng',
      color: 'from-amber-50 to-amber-100',
      baiHoc: [
        {
          id: 'bai-7-mo-hinh-dong-hoc-phan-tu-chat-khi',
          title: 'Bài 7. Mô hình động học phân tử chất khí',
          description: 'Mô hình phân tử và tính chất khí',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-8-dinh-luat-boyle',
          title: 'Bài 8. Định luật Boyle',
          description: 'Mối quan hệ giữa áp suất và thể tích',
          time: 45,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-9-dinh-luat-charles',
          title: 'Bài 9. Định luật Charles',
          description: 'Mối quan hệ giữa thể tích và nhiệt độ',
          time: 45,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-10-phuong-trinh-trang-thai-cua-khi-li-tuong',
          title: 'Bài 10. Phương trình trạng thái của khí lí tưởng',
          description: 'Phương trình trạng thái tổng quát',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-11-ap-suat-theo-mo-hinh-dong-hoc-phan-tu-dong-nang-phan-tu',
          title: 'Bài 11. Áp suất theo mô hình động học phân tử, động năng phân tử',
          description: 'Giải thích áp suất theo thuyết động học phân tử',
          time: 65,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 3,
      title: 'TỪ TRƯỜNG',
      description: 'Nghiên cứu từ trường và cảm ứng điện từ',
      color: 'from-indigo-50 to-indigo-100',
      baiHoc: [
        {
          id: 'bai-12-tu-truong',
          title: 'Bài 12. Từ trường',
          description: 'Khái niệm và đặc điểm của từ trường',
          time: 50,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-13-luc-tu-tac-dung-len-doan-day-dan-mang-dong-dien-cam-ung-tu',
          title: 'Bài 13. Lực từ tác dụng lên đoạn dây dẫn mang dòng điện; Cảm ứng từ',
          description: 'Lực từ và cảm ứng từ',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-14-tu-thong-hien-tuong-cam-ung-dien-tu',
          title: 'Bài 14. Từ thông, hiện tượng cảm ứng điện từ',
          description: 'Từ thông và định luật cảm ứng điện từ',
          time: 65,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-15-may-phat-dien-xoay-chieu-dai-cuong-ve-dong-dien-xoay-chieu',
          title: 'Bài 15. Máy phát điện xoay chiều - Đại cương về dòng điện xoay chiều',
          description: 'Máy phát điện và dòng điện xoay chiều',
          time: 70,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-16-ung-dung-cua-hien-tuong-cam-ung-dien-tu',
          title: 'Bài 16. Ứng dụng của hiện tượng cảm ứng điện từ',
          description: 'Các ứng dụng thực tế của cảm ứng điện từ',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-17-dien-tu-truong-mo-hinh-song-dien-tu',
          title: 'Bài 17. Điện từ trường, mô hình sóng điện từ',
          description: 'Điện từ trường và sóng điện từ',
          time: 65,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    },
    {
      id: 4,
      title: 'VẬT LÍ HẠT NHÂN',
      description: 'Nghiên cứu cấu trúc hạt nhân và phóng xạ',
      color: 'from-slate-50 to-slate-100',
      baiHoc: [
        {
          id: 'bai-18-cau-truc-hat-nhan',
          title: 'Bài 18. Cấu trúc hạt nhân',
          description: 'Cấu trúc và tính chất của hạt nhân nguyên tử',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-19-do-hut-khoi-va-nang-luong-lien-ket-hat-nhan',
          title: 'Bài 19. Độ hụt khối và năng lượng liên kết hạt nhân',
          description: 'Độ hụt khối và năng lượng liên kết',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-20-su-phan-hach-va-tong-hop-hat-nhan',
          title: 'Bài 20. Sự phân hạch và tổng hợp hạt nhân',
          description: 'Phân hạch và tổng hợp hạt nhân',
          time: 65,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-21-phong-xa-an-toan-phong-xa',
          title: 'Bài 21. Phóng xạ - An toàn phóng xạ',
          description: 'Hiện tượng phóng xạ và an toàn bức xạ',
          time: 60,
          difficulty: 'hard',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        },
        {
          id: 'bai-22-cong-nghiep-hat-nhan',
          title: 'Bài 22. Công nghiệp hạt nhân',
          description: 'Ứng dụng năng lượng hạt nhân trong công nghiệp',
          time: 55,
          difficulty: 'medium',
          sections: { lyThuyet: true, video: true, tracNghiem: true, moPhong: true },
          completed: false,
          bookmarked: false
        }
      ]
    }
  ]

  useEffect(() => {
    // Chọn dữ liệu chương theo lớp
    let selectedChuongData: Chuong[] = []
    
    if (idLop === '10') {
      selectedChuongData = chuongData
    } else if (idLop === '11') {
      selectedChuongData = chuongDataLop11
    } else if (idLop === '12') {
      selectedChuongData = chuongDataLop12
    }
    
    // Tìm chương hiện tại
    const chuong = selectedChuongData.find(c => c.id === Number(idChuong))
    if (chuong) {
      setCurrentChuong(chuong)
      setBaiHoc(chuong.baiHoc)
    }
    setLoading(false)
  }, [idLop, idChuong])

  const toggleBookmark = (baiId: string) => {
    setBaiHoc(prev => prev.map(bai => 
      bai.id === baiId ? { ...bai, bookmarked: !bai.bookmarked } : bai
    ))
  }

  const handleShare = (bai: BaiHoc) => {
    const shareData = {
      title: bai.title,
      text: bai.description,
      url: window.location.href
    }
    
    if (navigator.share) {
      navigator.share(shareData)
    } else {
      navigator.clipboard.writeText(window.location.href)
      toast.success('Đã sao chép liên kết!')
    }
  }

  const getSectionIcon = (sectionType: string) => {
    switch (sectionType) {
      case 'lyThuyet': return '📚'
      case 'video': return '🎥'
      case 'tracNghiem': return '🎯'
      case 'moPhong': return '🎮'
      default: return '📖'
    }
  }

  const getSectionText = (sectionType: string) => {
    switch (sectionType) {
      case 'lyThuyet': return 'Lý thuyết'
      case 'video': return 'Video'
      case 'tracNghiem': return 'Trắc nghiệm'
      case 'moPhong': return 'Mô phỏng'
      default: return 'Nội dung'
    }
  }

  const filteredBaiHoc = baiHoc.filter(bai => 
    filter === 'all' || bai.sections[filter as keyof typeof bai.sections]
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner text="Đang tải danh sách bài học..." />
        </div>
      </div>
    )
  }

  if (!currentChuong) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-700 mb-4">
              Không tìm thấy chương
            </h1>
            <button
              onClick={() => navigate(`/lop/${idLop}`)}
              className="btn-primary px-6 py-3 rounded-lg"
            >
              ← Về danh sách chương
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <button
              onClick={() => navigate(`/lop/${idLop}`)}
              className="flex items-center space-x-2 px-4 py-2 bg-white text-slate-800 rounded-lg hover:bg-gray-50 transition-colors border border-gray-300"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Quay lại</span>
            </button>
            
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-slate-700 mb-2">
                {currentChuong.title}
              </h1>
              <p className="text-lg text-gray-600">
                {currentChuong.description}
              </p>
            </div>
          </div>

          {/* Chapter Info Card */}
          <div className={`bg-gradient-to-r ${currentChuong.color} rounded-2xl p-6 text-slate-800 mb-8`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">
                  Chương {currentChuong.id}: {currentChuong.title}
                </h2>
                <p className="text-lg opacity-90 mb-4">
                  {currentChuong.description}
                </p>
                <div className="flex items-center space-x-6 text-sm">
                  <div className="flex items-center space-x-2">
                    <BookOpen className="w-5 h-5" />
                    <span>{currentChuong.baiHoc.length} bài học</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5" />
                    <span>
                      {currentChuong.baiHoc.reduce((total, bai) => total + bai.time, 0)} phút
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-6xl opacity-20">📚</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-700 mb-4">Lọc theo loại nội dung:</h3>
          <div className="flex space-x-2 overflow-x-auto pb-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'btn-primary'
                  : 'bg-gray-100 text-slate-800 hover:bg-gray-200'
              }`}
            >
              Tất cả
            </button>
            <button
              onClick={() => setFilter('lyThuyet')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'lyThuyet'
                  ? 'btn-primary'
                  : 'bg-gray-100 text-slate-800 hover:bg-gray-200'
              }`}
            >
              📚 Lý thuyết
            </button>
            <button
              onClick={() => setFilter('video')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'video'
                  ? 'btn-primary'
                  : 'bg-gray-100 text-slate-800 hover:bg-gray-200'
              }`}
            >
              🎥 Video
            </button>
            <button
              onClick={() => setFilter('tracNghiem')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'tracNghiem'
                  ? 'btn-primary'
                  : 'bg-gray-100 text-slate-800 hover:bg-gray-200'
              }`}
            >
              🎯 Trắc nghiệm
            </button>
            <button
              onClick={() => setFilter('moPhong')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'moPhong'
                  ? 'btn-primary'
                  : 'bg-gray-100 text-slate-800 hover:bg-gray-200'
              }`}
            >
              🎮 Mô phỏng
            </button>
          </div>
        </div>

        {/* Lessons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBaiHoc.map((bai) => (
            <Link
              key={bai.id}
              to={`/lop/${idLop}/chuong/${idChuong}/bai/${bai.id}`}
              className="block bg-white rounded-2xl shadow-lg overflow-hidden transform transition-transform duration-200 hover:scale-[1.02]"
            >
              <div className={`bg-gradient-to-r ${currentChuong.color} p-6 text-slate-800`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-3xl">📖</div>
                  <div className="flex items-center space-x-2">
                    {bai.bookmarked && (
                      <Bookmark className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                    )}
                    <span className="text-sm font-medium">
                      {bai.time} phút
                    </span>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">{bai.title}</h3>
                <p className="text-sm opacity-90">{bai.description}</p>
              </div>
              
              {/* Các phần nội dung */}
              <div className="p-6">
                <div className="space-y-3 mb-6">
                  {Object.entries(bai.sections).map(([sectionType, hasSection]) => (
                    <div key={sectionType} className="flex items-center space-x-2 text-sm">
                      <div className={`w-2 h-2 rounded-full ${hasSection ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <span className={`${hasSection ? 'text-gray-600' : 'text-gray-400'}`}>
                        {getSectionIcon(sectionType)} {getSectionText(sectionType)}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      bai.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                      bai.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {bai.difficulty === 'easy' ? 'Dễ' : 
                       bai.difficulty === 'medium' ? 'Trung bình' : 'Khó'}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        toggleBookmark(bai.id)
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        bai.bookmarked
                          ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Bookmark className={`w-4 h-4 ${bai.bookmarked ? 'fill-current' : ''}`} />
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        handleShare(bai)
                      }}
                      className="btn-secondary p-2 rounded-lg"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {filteredBaiHoc.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">
              Không có bài học nào
            </h3>
            <p className="text-gray-600">
              Không có bài học nào phù hợp với bộ lọc hiện tại.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default DanhSachChuong
